"use client";

import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export default function ProcessPage() {
  return (
    <div className="flex flex-col min-h-screen bg-background">
      <Header />
      <main className="flex-grow pt-32 pb-24">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <Badge variant="secondary" className="mb-6 px-4 py-1.5 text-[10px] uppercase tracking-[0.3em] font-black">How it works</Badge>
            <h1 className="font-headline text-6xl md:text-8xl font-black text-foreground mb-12 tracking-tight">
              Our <span className="text-primary italic font-normal">Process</span>
            </h1>

            <div className="space-y-8 mt-20">
              {[
                {
                  step: "01",
                  title: "Home Raising & ENS",
                  desc: "Our puppies are raised in the heart of our home. We use Early Neurological Stimulation (ENS) to help them develop strong immune systems and resilience. As they grow, they are introduced to household noises, varying textures, and daily handling by adults and children."
                },
                {
                  step: "02",
                  title: "Health & Wellness",
                  desc: "Health is our top priority. Every puppy is dewormed regularly and receives their first set of vaccinations and a comprehensive wellness exam by our licensed veterinarian before they go home. We provide you with a complete medical record and a health certificate upon adoption."
                },
                {
                  step: "03",
                  title: "Selection & Matching",
                  desc: "We assist families in choosing the right puppy based on temperament and lifestyle. While color and gender are important, personality fit is crucial for a lifelong bond. We provide videos, photos, and insights to help you make the best choice."
                },
                {
                  step: "04",
                  title: "Go Home Day",
                  desc: "Puppies go home once they are eating well on their own and have been cleared by our vet. On pickup day, you'll receive a 'Puppy Starter Kit' containing food, a blanket with the mother's scent, health records, and care instructions. We will spend time answering all your questions to ensure you feel confident and prepared."
                }
              ].map((item, idx) => (
                <Card key={idx} className="border-border shadow-soft bg-card overflow-hidden group hover:shadow-soft-hover transition-all duration-500">
                  <CardContent className="flex gap-8 items-start p-8">
                    <span className="font-headline text-5xl text-muted-foreground/20 group-hover:text-primary transition-colors duration-500">{item.step}</span>
                    <div>
                      <h2 className="text-2xl font-headline font-bold text-foreground mb-3">{item.title}</h2>
                      <p className="text-lg text-muted-foreground font-medium leading-relaxed">{item.desc}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="mt-20 prose prose-lg prose-stone max-w-none">
              <h2 className="text-4xl font-headline font-bold text-foreground mb-8">Preparing for Your Puppy</h2>
              <div className="bg-secondary/20 rounded-[2.5rem] p-10 border border-border/50">
                <p className="leading-relaxed mb-6 text-muted-foreground">
                  Bringing a new puppy home is an exciting milestone! To make the transition as smooth as possible, we recommend preparing your home in advance. Here is a checklist of essentials you should have ready:
                </p>
                <ul className="grid md:grid-cols-2 gap-4 text-muted-foreground font-medium list-none pl-0">
                  <li className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary" />
                    Hill’s Science Diet (Puppy Small & Mini)
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary" />
                    Adjustable crate with divider
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary" />
                    Food and water bowls (stainless steel recommended)
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary" />
                    Puppy-safe chew toys and plushies
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary" />
                    Collar, leash, and ID tag
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary" />
                    Enzyme cleaner for accidents
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
